import { z } from 'zod';

import { RotagenApiError } from '@/api/rotagen-api';
import type { RotagenValidationIssue } from '@/api/rotagen-api';

/**
 * Extracts the warnings from a failed commit so the UI can offer "Save anyway".
 *
 * Returns the warnings grouped by person, or `null` when the error is not a
 * warnings-only failure — a different error code, no issues, or any issue with
 * a severity other than `warning`. Treating a mixed failure as `null` keeps a
 * hard error from ever being offered for saving, even if the code is wrong.
 */
export function commitWarningGroupsFromError(error: unknown): CommitWarningGroup[] | null {
  if (!(error instanceof RotagenApiError) || error.code !== COMMIT_WARNINGS_CODE) {
    return null;
  }
  const { issues } = error;
  if (issues.length === 0 || issues.some((issue) => issue.severity !== 'warning')) {
    return null;
  }

  const groups = new Map<string | null, CommitWarningGroup>();
  for (const issue of issues) {
    const { worker, warning } = describeIssue(issue);
    const key = worker?.id ?? null;
    const group = groups.get(key) ?? { workerName: worker?.name ?? null, warnings: [] };
    if (!group.warnings.some((existing) => isSameWarning(existing, warning))) {
      group.warnings.push(warning);
    }
    groups.set(key, group);
  }

  // Warnings that name no worker go last, after the per-person groups.
  const unnamed = groups.get(null);
  groups.delete(null);
  return [...groups.values(), ...(unnamed ? [unnamed] : [])];
}

/**
 * Splits an issue into the worker it concerns and the warning to show. A
 * context that is missing or does not match the expected shape yields just the
 * message, so an unexpected payload never breaks the warnings step.
 */
function describeIssue(issue: RotagenValidationIssue): {
  worker: { id: string; name?: string } | undefined;
  warning: CommitWarning;
} {
  const parsed = IssueContextSchema.safeParse(issue.context);
  if (!parsed.success) {
    return { worker: undefined, warning: { message: issue.message } };
  }
  const { worker, rule, currentValue, comparatorValue, unit } = parsed.data;
  return {
    worker,
    warning: {
      message: issue.message,
      ...(rule !== undefined && { rule }),
      ...(currentValue !== undefined && { currentValue }),
      ...(comparatorValue !== undefined && { comparatorValue }),
      ...(unit !== undefined && { unit }),
    },
  };
}

/** Whether two warnings say the same thing, so repeats (one per deleted draft) show once. */
function isSameWarning(first: CommitWarning, second: CommitWarning): boolean {
  return (
    first.message === second.message &&
    first.rule === second.rule &&
    first.currentValue === second.currentValue &&
    first.comparatorValue === second.comparatorValue &&
    first.unit === second.unit
  );
}

/** Warnings about one person, or the warnings that name nobody when `workerName` is `null`. */
export interface CommitWarningGroup {
  /** The person the warnings concern, or `null` for warnings that name no one. */
  workerName: string | null;
  /** The distinct warnings for this person, in the order rotagen reported them. */
  warnings: CommitWarning[];
}

/** A single ignorable validation warning, as much of its detail as rotagen supplied. */
export interface CommitWarning {
  /** Rotagen's technical description of the issue; the fallback text when nothing richer is known. */
  message: string;
  /** Stable identifier for the breached check, e.g. `contractualHours`. */
  rule?: string;
  /** The measured value, e.g. the hours the person would be rostered for. */
  currentValue?: number;
  /** The value the measurement is compared against, e.g. the contracted hours. */
  comparatorValue?: number;
  /** What `currentValue` and `comparatorValue` are measured in. */
  unit?: 'hours' | 'days';
}

/**
 * Error code rotagen returns when a commit was rejected only by ignorable
 * Rotaready validation issues, so the commit can be retried with
 * `ignoreWarnings`.
 */
const COMMIT_WARNINGS_CODE = 'COMMIT_WARNINGS';

/** The `context` rotagen attaches to each commit validation issue. */
const IssueContextSchema = z.object({
  worker: z.object({ id: z.string(), name: z.string().optional() }).optional(),
  rule: z.string().optional(),
  currentValue: z.number().optional(),
  comparatorValue: z.number().optional(),
  unit: z.enum(['hours', 'days']).optional(),
});
